import React, { useState, useMemo, useEffect } from 'react';
import './BlockPalette.css';

interface BlockPaletteProps {
    selectedTexture: string | null;
    onTextureSelect: (texture: string) => void;
}

const BlockPalette: React.FC<BlockPaletteProps> = ({ selectedTexture, onTextureSelect }) => {
    const [searchPattern, setSearchPattern] = useState('');
    const [searchError, setSearchError] = useState('');
    const [availableTextures, setAvailableTextures] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);

    // Load all available textures from manifest
    useEffect(() => {
        const loadTextures = async () => {
            try {
                const response = await fetch('/textures/manifest.json');
                if (response.ok) {
                    const text = await response.text();
                    const textures = text
                        .trim()
                        .split('\n')
                        .filter(texture => texture.endsWith('.png'))
                        .filter(texture => !texture.includes('.mcmeta'))
                        .sort();
                    setAvailableTextures(textures);
                } else {
                    throw new Error('Manifest not found');
                }
            } catch (error) {
                console.warn('Failed to load texture manifest, using fallback list');
                // Fallback to a comprehensive hardcoded list
                const fallbackTextures = [
                    'anvil_base.png', 'beacon.png', 'bedrock.png', 'bookshelf.png', 'brick.png',
                    'coal_ore.png', 'cobblestone.png', 'dirt.png', 'diamond_ore.png', 'emerald_ore.png',
                    'end_stone.png', 'glowstone.png', 'gold_ore.png', 'grass_top.png', 'gravel.png',
                    'iron_ore.png', 'lapis_ore.png', 'netherrack.png', 'obsidian.png', 'redstone_ore.png',
                    'sand.png', 'stone.png', 'wool_colored_white.png', 'wool_colored_black.png',
                    'wool_colored_red.png', 'wool_colored_blue.png', 'wool_colored_green.png',
                    'wool_colored_yellow.png', 'planks_oak.png', 'log_oak.png', 'glass.png'
                ];
                setAvailableTextures(fallbackTextures);
            } finally {
                setLoading(false);
            }
        };

        loadTextures();
    }, []);

    const filteredTextures = useMemo(() => {
        if (!searchPattern.trim()) {
            setSearchError('');
            return availableTextures;
        }

        try {
            const regex = new RegExp(searchPattern, 'i');
            setSearchError('');
            return availableTextures.filter(texture => 
                regex.test(texture.replace('.png', ''))
            );
        } catch (error) {
            setSearchError('Invalid regex pattern');
            return availableTextures;
        }
    }, [searchPattern, availableTextures]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchPattern(e.target.value);
    };

    const clearSearch = () => {
        setSearchPattern('');
        setSearchError('');
    };

    if (loading) {
        return (
            <div className="block-palette">
                <h3 className="palette-title">Block Palette</h3>
                <div className="loading-message">Loading textures...</div>
            </div>
        );
    }

    return (
        <div className="block-palette">
            <h3 className="palette-title">Block Palette</h3>
            
            <div className="search-container">
                <div className="search-input-wrapper">
                    <input
                        type="text"
                        placeholder="Search blocks (regex supported)..."
                        value={searchPattern}
                        onChange={handleSearchChange}
                        className={`search-input ${searchError ? 'error' : ''}`}
                    />
                    {searchPattern && (
                        <button 
                            onClick={clearSearch}
                            className="clear-search-btn"
                            title="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>
                {searchError && (
                    <div className="search-error">{searchError}</div>
                )}
                <div className="search-info">
                    {filteredTextures.length} of {availableTextures.length} blocks
                    {searchPattern && (
                        <span className="search-examples">
                            • Try: "ore", "stone|brick", "^[a-c]", "wool.*red", etc.
                        </span>
                    )}
                </div>
            </div>

            <div className="palette-grid">
                {filteredTextures.map((texture) => (
                    <button
                        key={texture}
                        className={`palette-block ${selectedTexture === texture ? 'selected' : ''}`}
                        onClick={() => onTextureSelect(texture)}
                        title={texture.replace('.png', '').replace(/_/g, ' ')}
                    >
                        <img
                            src={`/textures/${texture}`}
                            alt={texture.replace('.png', '')}
                            className="palette-block-image"
                            onError={(e) => {
                                // Hide broken images
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    </button>
                ))}
                {filteredTextures.length === 0 && searchPattern && !searchError && (
                    <div className="no-results">No blocks match your search pattern</div>
                )}
            </div>
        </div>
    );
};

export default BlockPalette;